import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import { Footer, HamburgerMenu } from '@/components';
import ActionButtons from '@/components/ActionButtons';
import { ChevronRightIcon } from '@/components/icons';
import { useLoadingContext, useAuth } from '@/hooks';
import { 
  useEventData, 
  useEventSchedules, 
  useEventGallery, 
  useEventScheduleView, 
  useEventNavigation,
  useEventArtists
} from '@/features/events/hooks';
import { 
  CustomCarousel,
  AboutSection,
  ArtistsSection,
  SponsorsSection,
  CalendarView,
  BookingSection,
  EventPagination
} from '@/features/events/components';
import { getSignedMediaUrl } from '@/lib/utils';
import { 
  SchedulesSkeleton, 
  ViewModeToggle, 
  PastDatesToggle, 
  EventHeader, 
  ErrorState 
} from './EventSchedulePage/components';

const EventSchedulePage: React.FC = () => {
  const { eventId, handle } = useParams<{ eventId: string; handle: string }>();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isAuthenticated } = useAuth();
  
  // Custom hooks for data fetching
  const { eventDetails, loading, error } = useEventData(eventId);
  const { artists } = useEventArtists(eventId);

  // Custom hooks for schedule management
  const { 
    schedules, 
    schedulesLoading, 
    showPastSchedules, 
    setShowPastSchedules 
  } = useEventSchedules(eventDetails, eventId);

  // Custom hooks for view state
  const {
    viewMode,
    setViewMode,
    currentMonth,
    setCurrentMonth,
    availableMonths,
    paginatedSchedules
  } = useEventScheduleView(schedules);

  // Custom hooks for navigation
  const { 
    handleBookTable, 
    handleBookTickets, 
    eventInfo, 
    businessInfo, 
    clubHandle 
  } = useEventNavigation(eventDetails, eventId, handle);

  // Gallery items for carousel
  const galleryItems = useEventGallery(eventDetails);
  
  // Get timezone from first schedule's timezone_id
  const businessTimeZone = schedules[0]?.timezone_id;

  // Loading state management
  React.useEffect(() => {
    if (loading) {
      startLoading('event-schedule-data');
    } else {
      stopLoading('event-schedule-data');
    }
    
    return () => {
      stopLoading('event-schedule-data');
    };
  }, [loading, startLoading, stopLoading]);

  // Loading state
  if (loading) {
    return <div className="bg-black min-h-screen" />;
  }

  // Error state
  if (error || !eventDetails || !eventInfo) {
    return (
      <ErrorState 
        error={error} 
        onGoBack={() => navigate(-1)} 
      />
    );
  }

  return (
    <>
    <div className="min-h-screen bg-black text-white font-sans">
      <HamburgerMenu clubHandle={clubHandle} isAuthenticated={isAuthenticated} />
      
      {/* Floating Action Buttons */}
      <div className="fixed top-7 right-[5.5rem] z-[99] flex items-center gap-3">
        <ActionButtons
          entityId={eventId || ''}
          entityName={eventInfo.name}
          entityType="events"
          showBell={true}
        />
      </div>
      
      {/* Breadcrumb Navigation */}
      <div className="relative z-10 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <nav className="text-sm text-gray-400 flex items-center">
            <button 
              onClick={() => navigate(`/venues/${businessInfo?.handle}`)}
              className="hover:text-white transition-colors cursor-pointer"
            >
              {businessInfo?.handle || 'Venue'}
            </button>
            <ChevronRightIcon className="mx-2 w-4 h-4" />
            <span className="text-white">{eventInfo.name}</span>
          </nav>
        </div>
      </div>

      {/* Background Image */}
      <div 
        className="absolute top-0 left-0 h-screen w-full bg-cover bg-center opacity-20" 
        style={{
          backgroundImage: `url('${eventInfo.card_image_id && eventInfo.card_image_id !== '00000000-0000-0000-0000-000000000000' ? getSignedMediaUrl(eventInfo.card_image_id, 'image').url : ''}')`
        }}
      />
      <div className="absolute top-0 left-0 h-screen w-full bg-gradient-to-b from-black/10 via-black to-black" />

      <div className="relative">
        {/* Header Section */}
        <EventHeader eventInfo={eventInfo} />

        {/* Main Content Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-x-8 lg:gap-x-16 min-h-[60vh]">
          <div className="lg:col-span-4 flex items-center">
            {businessInfo && (
              <AboutSection eventInfo={eventInfo} businessInfo={businessInfo} />
            )}
          </div>

          <div className="lg:col-span-8 hidden lg:block">
            <div className="sticky top-24">
              <CustomCarousel items={galleryItems} />
            </div>
          </div>
        </div>

        {/* Featured Artists and Sponsors Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {artists && artists.length > 0 && (
              <div>
                <ArtistsSection artists={artists} clubHandle={clubHandle || undefined} />
              </div>
            )}
            
            {eventInfo.sponsors && eventInfo.sponsors.length > 0 && (
              <div className="flex items-center justify-center">
                <SponsorsSection sponsors={eventInfo.sponsors} />
              </div>
            )}
          </div>
        </div>

        {/* Schedule Section */}
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-20">
          <div className="relative flex justify-center items-center mb-10 min-h-[48px]">
            <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
            <PastDatesToggle 
              showPastSchedules={showPastSchedules} 
              onToggle={setShowPastSchedules} 
            />
          </div>
          
          <AnimatePresence mode="wait">
            {schedulesLoading ? (
              <SchedulesSkeleton />
            ) : viewMode === 'calendar' ? (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <CalendarView 
                  schedules={schedules} 
                  businessTimeZone={businessTimeZone}
                  onBookTickets={handleBookTickets}
                  onBookTables={handleBookTable}
                />
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <h2 className="text-4xl font-serif font-medium text-white text-center mb-10">
                  Book Your Date
                </h2>
                {schedules.length > 0 && currentMonth && (
                  <div className="mb-10">
                    <EventPagination
                      currentMonth={currentMonth}
                      availableMonths={availableMonths}
                      totalItems={paginatedSchedules.length}
                      onMonthChange={setCurrentMonth}
                    />
                  </div>
                )}
                <BookingSection
                  schedules={paginatedSchedules}
                  onBookTable={handleBookTable}
                  onBookTickets={handleBookTickets}
                  businessTimeZone={businessTimeZone}
                  showHeading={false}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      <Footer />
    </div>
    </>
  );
};

export default EventSchedulePage;
